import { EVENTS } from "../../../js/constants";

class Lightbox {
  /**
   * @param {HTMLElement} element - lightbox
   */
  constructor(element) {
    this.element = element;
    this.openClass = "is-open";
    this.previousFocusedElement = "";
    this.lightboxCloser = this.element.querySelector(
      ".js-Lightbox-closeButton"
    );
    this.lightboxOpener = this.element.querySelector(".js-Lightbox-opener");
    this.lightboxOverlay = this.element.querySelector(".js-Lightbox-overlay");
    this.lightboxLink = this.element.querySelector(".js-Lightbox-link");
    this.focusableElements = document.querySelectorAll(
      "a, button, input, select, textarea"
    );

    this.openLightbox = this.openLightbox.bind(this);
    this._closeLightbox = this._closeLightbox.bind(this);
    this._closeLightboxViaKeyboard = this._closeLightboxViaKeyboard.bind(this);

    if (this.lightboxOpener) {
      this._bindOpenButton();
    }
  }

  /**
   * binds event listeners on lightbox opener
   *
   * @private
   */
  _bindOpenButton() {
    this.lightboxOpener.addEventListener("click", this.openLightbox);
  }

  /**
   * opens lightbox and dispatches `MODAL_OPEN` event
   *
   */
  openLightbox() {
    this.element.classList.add(this.openClass);
    this.element.dispatchEvent(
      new CustomEvent(EVENTS.MODAL_OPEN, {
        bubbles: true,
        detail: { isOpen: true },
      })
    );

    this.previousFocusedElement = document.activeElement;
    this._toggleElementsFocus();
    this._bindUserEvents();
    this._focusLink();
  }

  /**
   * bind user events
   *
   * @private
   */
  _bindUserEvents() {
    if (this.lightboxCloser) {
      this.lightboxCloser.addEventListener("click", this._closeLightbox);
    }
    if (this.lightboxOverlay) {
      this.lightboxOverlay.addEventListener("click", this._closeLightbox);
    }
    document.addEventListener("keyup", this._closeLightboxViaKeyboard);
  }

  /**
   * unbind user events
   *
   * @private
   */
  _unbindUserEvents() {
    if (this.lightboxCloser) {
      this.lightboxCloser.removeEventListener("click", this._closeLightbox);
    }
    if (this.lightboxOverlay) {
      this.lightboxOverlay.removeEventListener("click", this._closeLightbox);
    }
    document.removeEventListener("keyup", this._closeLightboxViaKeyboard);
  }

  /**
   * closes lightbox and dispatches `MODAL_OPEN` event
   *
   * @private
   */
  _closeLightbox() {
    this.element.classList.remove(this.openClass);
    if (this.previousFocusedElement) {
      this.previousFocusedElement.focus();
    }

    this._toggleElementsFocus();

    this.element.dispatchEvent(
      new CustomEvent(EVENTS.MODAL_OPEN, {
        bubbles: true,
        detail: { isOpen: false },
      })
    );

    this._unbindUserEvents();
  }

  /**
   * closes lightbox when Esc key has been pressed
   *
   * @param {object} e - event
   * @private
   */
  _closeLightboxViaKeyboard(e) {
    if (e.key === "Escape" || e.key === "Esc" || e.keyCode === 27) {
      this._closeLightbox();
    }
  }

  /**
   * focus the lightbox link if available
   *
   * @private
   */
  _focusLink() {
    if (this.lightboxLink) {
      this.lightboxLink.focus();
    } else if (this.lightboxCloser) {
      this.lightboxCloser.focus();
    }
  }

  /**
   * Add or remove focus of the focusable elements outside lightbox
   *
   * @private
   */
  _toggleElementsFocus() {
    this.focusableElements.forEach((element) => {
      if (!this.element.contains(element)) {
        if (element.tabIndex === -1) {
          element.removeAttribute("tabindex");
        } else {
          element.setAttribute("tabindex", "-1");
        }
      }
    });
  }
}

export default Lightbox;
